// frontend/src/components/SalesOverview.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Calendar,
  TrendingUp,
  Package,
  Edit3,
  X,
  Check,
  Search,
  Download,
  Trash2,
} from 'lucide-react';
import { salesAPI } from '../services/api';
import toast from 'react-hot-toast';

function SalesOverview() {
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('week');
  const queryClient = useQueryClient();

  // Delete order mutation
  const deleteOrderMutation = useMutation(
    (orderId) => salesAPI.deleteOrder(orderId),
    {
      onSuccess: (_, orderId) => {
        queryClient.invalidateQueries(['sales-orders']);
        toast.success('Salgsordre slettet!');
      },
      onError: (error) => {
        toast.error(`Fejl ved sletning: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleDeleteOrder = (orderId) => {
    if (window.confirm('Er du sikker på, at du vil slette denne ordre? Dette vil frigive alle reserverede varer.')) {
      deleteOrderMutation.mutate(orderId);
    }
  };

  // Queries
  const { data: salesOrders = [], isLoading } = useQuery(
    ['sales-orders', { search: searchTerm, range: dateRange }],
    () => salesAPI.getOrders({ search: searchTerm, range: dateRange }),
    {
      keepPreviousData: true
    }
  );

  // Mutation for updating orders
  const updateOrderMutation = useMutation(
    ({ orderId, data }) => salesAPI.updateOrder(orderId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sales-orders']);
        toast.success('Salgsordre opdateret!');
        setEditingOrder(null);
        setEditForm({});
      },
      onError: (error) => {
        toast.error(`Fejl ved opdatering: ${error.response?.data?.message || error.message}`);
      }
    }
  );

  const handleEditClick = (order) => {
    setEditingOrder(order.id);
    setEditForm({
      notes: order.notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    updateOrderMutation.mutate({
      orderId: editingOrder,
      data: {
        notes: editForm.notes
      }
    });
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards - Empty for now */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Statistik 1</p>
              <p className="text-2xl font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Statistik 2</p>
              <p className="text-2xl font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Statistik 3</p>
              <p className="text-2xl font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Statistik 4</p>
              <p className="text-2xl font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Søg efter ordre ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="today">I dag</option>
                <option value="week">Denne uge</option>
                <option value="month">Denne måned</option>
                <option value="quarter">Dette kvartal</option>
                <option value="year">Dette år</option>
                <option value="all">Alle</option>
              </select>

              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produkt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Antal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leveringsdato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bemærkninger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(salesOrders) && salesOrders.map((order) => {
                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        #{order.orderNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {order.items && order.items.length > 0 ? (
                          order.items.map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="text-gray-900 font-medium">
                                {item?.variant?.product?.name || 'Ukendt produkt'}
                              </div>
                              <div className="text-gray-500 text-xs">
                                {item?.variant?.sku || '-'}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500">Ingen produkter</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {order.items && order.items.length > 0 ? (
                          order.items.map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <div className="text-gray-900">
                                {item?.quantity || 0} stk
                              </div>
                              <div className="text-xs text-transparent">
                                &nbsp;
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500">-</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.deliveryDate ? formatDate(order.deliveryDate) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {editingOrder === order.id ? (
                        <textarea
                          value={editForm.notes}
                          onChange={(e) => handleInputChange('notes', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows="2"
                        />
                      ) : (
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {order.notes || '-'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingOrder === order.id ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateOrderMutation.isLoading}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditClick(order)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            className="text-red-600 hover:text-red-900"
                            disabled={deleteOrderMutation.isLoading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {salesOrders.length === 0 && (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen salgsordrer</h3>
            <p className="mt-1 text-sm text-gray-500">
              Ingen ordrer matcher dine søgekriterier.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesOverview;
